import _ from "lodash";
import React, { useEffect, useState, useImperativeHandle } from "react";
import { usePrevious, useI18n } from "Hooks";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";

// DS
import {
  ButtonDisclosure,
  Dropdown,
  DropdownMenu,
  IconButton,
  Modal,
  Tooltip,
} from "@toddle-edu/ds-web";
import { CloseOutlined, VoiceWaveOutlined } from "@toddle-edu/ds-icons";

// Modules
import { updateAIConversation } from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import { getAIPromptSetsQuery } from "modules/CommonQuery";
import { getAIPromptSetsFromCache } from "modules/CommonGraphqlHelpers";
import ACLStore from "lib/aclStore";
import {
  getUserId,
  getCurrentCurriculumProgramType,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";

// Utils
import { preferredLanguageFieldOptions } from "../../modules/Utils";
import {
  getOverriddenModelFromLocalStorage,
  setOverriddenModelInLocalStorage,
} from "Utils/AIUtils";
import { getSettingValue } from "Utils/ConfigurationUtils";

// third party
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";

// Constants
import { colors } from "Constants";
import { ASSESSMENT } from "Constants/stringConstants";
import { getModelOptions } from "AppComponents/GPTPlayground/modules/Utils";

// Scss
import classes from "./ConfigureAIVoiceV2.scss";
import { shouldAllowOverridingAIModel } from "config";

// ConfigureAIVoiceV2 just contains language and prompt set selection

const modelOptions = [
  { key: "default", label: "default" },
  ...getModelOptions(),
];

const AIVoiceContainer = props => {
  const {
    t,
    headerSubtitle,
    isPromptSetSelectionEnabled,
    promptSetsOptions,
    isLoading,
    language,
    setLanguage,
    preferredLanguageOptions,
    selectedPromptSet,
    onUpdateSetting,
    onUpdateAIPromptSetSetting,
    setSelectedPromptSet,
    modalCloseHandler,
    entityType,
    updateAIConversation,
    conversationId,
    showLanguageDropdown,
    promptSetField,
  } = props;

  // Show model override dropdown only for staging env
  const shouldShowOverrideModelOptions =
    shouldAllowOverridingAIModel &&
    ACLStore.can("Common:EnableOverrideAIModelOptions");

  const [overriddenModel, setOverriddenModelValue] = useState(
    getOverriddenModelFromLocalStorage(promptSetField)
  );

  useEffect(() => {
    setOverriddenModelInLocalStorage({ overriddenModel, promptSetField });
  }, [overriddenModel]);

  const renderFieldHeader = headerText => {
    return <div className={classes.fieldHeaderText}>{headerText}</div>;
  };

  // this will make dropdown menu to open outside of modal
  const getPopupContainer = () => document.body;

  const onChangeLanguage = ({ key }) => {
    setLanguage(key);
    onUpdateSetting(key);

    // when lanugage is changes then update the conversation metadata as well
    if (conversationId && entityType === ASSESSMENT) {
      const selectedLanguage = _.find(preferredLanguageOptions, {
        key,
      });

      if (selectedLanguage) {
        updateAIConversation({
          input: {
            id: conversationId,
            metadata: {
              language: _.get(selectedLanguage, "label"),
              languageCode: _.get(selectedLanguage, "lng"),
            },
          },
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className={classes.overlayContainer}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={classes.overlayContainer}>
      <div className={classes.headerContainer}>
        <div className={classes.headerLeftContainer}>
          <div className={classes.iconContainer}>
            <VoiceWaveOutlined
              style={{
                color: colors.blue300,
              }}
            />
          </div>
          <div className={classes.headerTextContainer}>
            <div className={classes.headerTitle}>{t("common:my_voice")}</div>
            <div className={classes.headerSubtitle}>{headerSubtitle}</div>
          </div>
        </div>
        <IconButton
          icon={<CloseOutlined variant="subtle" />}
          _legacy_variant="plain"
          variant="neutral"
          type="plain"
          onClick={modalCloseHandler}
        />
      </div>
      <div className={classes.bodyContainer}>
        {showLanguageDropdown ? (
          <div className={classes.fieldContainer}>
            {renderFieldHeader(t("common:preferred_language"))}
            <Dropdown
              overlay={
                <DropdownMenu
                  value={language}
                  options={preferredLanguageOptions}
                  onClick={onChangeLanguage}
                />
              }
              getPopupContainer={getPopupContainer}
            >
              <ButtonDisclosure
                _legacy_variant="outlined-subtle"
                variant="neutral"
                type="outlined"
                isFullWidth={true}
              >
                {
                  _.find(preferredLanguageOptions, {
                    key: language,
                  })?.label
                }
              </ButtonDisclosure>
            </Dropdown>
          </div>
        ) : null}
        {isPromptSetSelectionEnabled && !_.isEmpty(promptSetsOptions) && (
          <div className={classes.fieldContainer}>
            {renderFieldHeader(t("common:prompt_set"))}
            <Dropdown
              overlay={
                <DropdownMenu
                  value={selectedPromptSet}
                  options={promptSetsOptions}
                  onClick={e => {
                    setSelectedPromptSet(e.key);
                    onUpdateAIPromptSetSetting(e.key);
                  }}
                />
              }
              getPopupContainer={getPopupContainer}
            >
              <ButtonDisclosure
                _legacy_variant="outlined-subtle"
                variant="neutral"
                type="outlined"
                isFullWidth={true}
              >
                {
                  _.find(promptSetsOptions, {
                    key: selectedPromptSet,
                  })?.label
                }
              </ButtonDisclosure>
            </Dropdown>
          </div>
        )}
        {shouldShowOverrideModelOptions && (
          <div className={classes.fieldContainer}>
            {renderFieldHeader("Override Model")}
            <Dropdown
              overlay={
                <DropdownMenu
                  value={overriddenModel}
                  options={modelOptions}
                  onClick={e => {
                    setOverriddenModelValue(e.key);
                  }}
                />
              }
              getPopupContainer={getPopupContainer}
            >
              <ButtonDisclosure
                _legacy_variant="outlined-subtle"
                variant="neutral"
                type="outlined"
                isFullWidth={true}
              >
                {_.find(modelOptions, { key: overriddenModel })?.label ||
                  "Default"}
              </ButtonDisclosure>
            </Dropdown>
          </div>
        )}
      </div>
    </div>
  );
};

const ConfigureAIVoiceV2 = props => {
  const {
    size = "medium",
    isDisabled = false,
    customRef,
    preferredLanguageOptions,
    configurationValue,
    selectedLanguage,
    getConfiguration,
    selectedAIPromptSets,
    promptSetField,
    promptSetsOptions,
    userId,
    updateSettings,
    showLanguageDropdown,
    onSettingsUpdate,
    iconButtonProps,
    isInsightsAIIdentityBasedVoiceConfigEnabled,
  } = props;

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [selectedPromptSet, setSelectedPromptSet] = useState(null);

  const [language, setLanguage] = useState("");
  const { t } = useI18n(["common"]);

  const previousSelectedLanguage = usePrevious(selectedLanguage);

  // set previous selected language of user as preferred voice language
  useEffect(() => {
    if (
      showLanguageDropdown &&
      !_.isEmpty(selectedLanguage) &&
      !_.isEqual(previousSelectedLanguage, selectedLanguage) &&
      (_.isEmpty(language) || !_.isEqual(language, selectedLanguage))
    ) {
      setLanguage(selectedLanguage);

      // if previous selection does not exist then set default value (platform language) as preferred voice language
      if (_.isEmpty(configurationValue)) {
        onUpdateSetting(selectedLanguage);
        getConfiguration?.refetch();
      }
    }
  }, [language, selectedLanguage, configurationValue]);

  // set previous selected prompt set of user as selected prompt set
  useEffect(() => {
    if (_.isNil(selectedPromptSet)) {
      let key = null;
      if (!_.isEmpty(selectedAIPromptSets?.[promptSetField])) {
        key = _.get(selectedAIPromptSets, promptSetField);
      } else if (!_.isEmpty(promptSetsOptions)) {
        // if previous selection does not exist then first option of prompt set as selected prompt set
        key = _.first(promptSetsOptions)?.key;
        onUpdateAIPromptSetSetting(key);
      }

      if (key) {
        setSelectedPromptSet(key);
      }
    }
  }, [
    promptSetField,
    promptSetsOptions,
    selectedAIPromptSets,
    selectedPromptSet,
  ]);

  useImperativeHandle(
    customRef,
    () => ({
      preferredVoiceObj: { language },
      preferredLanguageOptions,
    }),
    [language, preferredLanguageOptions]
  );

  const onUpdateSetting = async value => {
    try {
      if (value) {
        const settings = [
          {
            name: "ConfigureRTEAIVoiceLanguage",
            value,
          },
        ];

        await updateSettings({
          id: userId,
          type: "user",
          settings,
          onSettingsUpdate,
          showToastMsg: false,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onUpdateAIPromptSetSetting = async value => {
    try {
      if (isInsightsAIIdentityBasedVoiceConfigEnabled) {
        await updateSettings({
          id: userId,
          type: "user",
          settings: [
            {
              name: "AIPromptSetInsights",
              value: JSON.stringify({
                ...selectedAIPromptSets,
                [promptSetField]: value,
              }),
            },
          ],
          mutationVariablesSrc: "IDENTITY_ONLY",
          showToastMsg: false,
        });
      } else {
        await updateSettings({
          id: userId,
          type: "user",
          settings: [
            {
              name: "AIPromptSet",
              value: JSON.stringify({
                ...selectedAIPromptSets,
                [promptSetField]: value,
              }),
            },
          ],
          showToastMsg: false,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const updateDropdownVisibility = () => {
    setIsDropdownVisible(prevState => !prevState);
  };

  const modalCloseHandler = () => {
    setIsDropdownVisible(false);
    getConfiguration?.refetch();
  };

  return (
    <>
      <Tooltip
        placement="bottom"
        tooltip={t("common:toddle_ai_voice")}
        showArrow={true}
      >
        <IconButton
          size={size}
          _legacy_variant={"plain"}
          variant="neutral"
          type="plain"
          disabled={isDisabled}
          isActivated={isDropdownVisible}
          icon={<VoiceWaveOutlined />}
          onClick={updateDropdownVisibility}
          {...iconButtonProps}
        />
      </Tooltip>
      <Modal
        width="max-content"
        height="max-content"
        isOpen={isDropdownVisible}
        onClose={modalCloseHandler}
      >
        <AIVoiceContainer
          {...props}
          t={t}
          language={language}
          setLanguage={setLanguage}
          preferredLanguageOptions={preferredLanguageOptions}
          selectedPromptSet={selectedPromptSet}
          setSelectedPromptSet={setSelectedPromptSet}
          onUpdateSetting={onUpdateSetting}
          onUpdateAIPromptSetSetting={onUpdateAIPromptSetSetting}
          modalCloseHandler={modalCloseHandler}
        />
      </Modal>
    </>
  );
};

ConfigureAIVoiceV2.displayName = "ConfigureAIVoiceV2";

ConfigureAIVoiceV2.defaultProps = {
  showLanguageDropdown: true,
  iconButtonProps: {},
};

const mapActionCreators = {
  updateSettings,
  updateAIConversation,
};

const mapStateToProps = (state, ownProps) => {
  const {
    t,
    i18n,
    promptSetEntityType,
    entityType,
    excludeCurriculumTypeFromPromptSetField = false,
    showLanguageDropdown = true,
  } = ownProps;

  const userId = getUserId(state);
  const currentCurriculumProgramType = getCurrentCurriculumProgramType(state);
  const currentCurriculumProgramId = getSelectedCurriculumId(state);

  const configurationName = "ConfigureRTEAIVoiceLanguage";

  let configurationValue = null;
  let selectedAIPromptSets = null;

  const isEntityTypeInsightsPlayground =
    promptSetEntityType === "INSIGHTS_PLAYGROUND";

  const isInsightsAIIdentityBasedVoiceConfigEnabled =
    ACLStore.can("IdentityAccess:IdentityLevelPromptSetSelection") &&
    isEntityTypeInsightsPlayground;

  try {
    if (showLanguageDropdown) {
      configurationValue = _.toString(ACLStore.can(configurationName));
    }

    selectedAIPromptSets = isInsightsAIIdentityBasedVoiceConfigEnabled
      ? getSettingValue({
          name: "AIPromptSetInsights",
          readFromUserAndOrgLevel: true,
        })
      : getSettingValue({
          name: "AIPromptSet",
          readFromUserAndOrgLevel: true,
        });
  } catch (err) {
    console.error(err);
  }

  const preferredLanguageOptions = preferredLanguageFieldOptions({
    t,
    showAllGlobalVoiceLanguages: true,
  });

  let selectedLanguage = configurationValue;

  // if configurationValue is empty, set default value to true which will set platform language as AI voice language
  if (_.isEmpty(configurationValue)) {
    const languageId = _.find(preferredLanguageOptions, language => {
      return _.get(language, "lng") === _.get(i18n, "language", "en");
    })?.key;

    if (languageId) selectedLanguage = _.toString(languageId);
  }
  return {
    userId,
    currentCurriculumProgramType,
    currentCurriculumProgramId,
    isPromptSetSelectionEnabled:
      isEntityTypeInsightsPlayground ||
      getSettingValue({
        name: "Common:EnableAIPromptSetSelection",
        readFromUserAndOrgLevel: true,
      }),
    promptSetFilters: {
      entityType: promptSetEntityType,
      curriculumProgramId: currentCurriculumProgramId,
    },
    promptSetField:
      currentCurriculumProgramType && !excludeCurriculumTypeFromPromptSetField
        ? `${promptSetEntityType || entityType}_${currentCurriculumProgramType}`
        : promptSetEntityType || entityType,
    selectedAIPromptSets,
    isData: true,
    isLoading: false,
    configurationName,
    selectedLanguage,
    configurationValue,
    preferredLanguageOptions,
    isInsightsAIIdentityBasedVoiceConfigEnabled,
  };
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getAIPromptSetsQuery, {
    name: "getAIPromptSets",
    skip: ({ isPromptSetSelectionEnabled, promptSetFilters }) =>
      !isPromptSetSelectionEnabled || !promptSetFilters.entityType,
    options: ({ promptSetFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: { filters: promptSetFilters },
    }),
    props({
      getAIPromptSets: { variables, networkStatus },
      ownProps: { isData, isLoading },
    }) {
      const queryData = getAIPromptSetsFromCache(variables);
      const promptSets = _.get(queryData, "promptSets", []);

      const promptSetsOptions = _.map(promptSets, ({ id, label }) => ({
        key: id,
        label,
      }));

      return {
        promptSets,
        promptSetsOptions,
        isData: isData && !_.isEmpty(queryData),
        isLoading: isLoading || _.includes([1, 2], networkStatus),
      };
    },
  })
)(ConfigureAIVoiceV2);
